import React, { useState } from "react";

import { CROP_TYPE_FCPO, PRACTICE_SLUG, PracticeSlug } from "models";

import { Col, FilterSet, FilterSetProps, InputSearch, Option, Row, Select } from "components";

type OnFilterChange = (key: string) => void;
type OnSortChange = (sort: { sortBy: "name" | "size" | "number"; sortOrder: "asc" | "desc" } | undefined) => void;

export type SortOption = {
  sortBy: "name" | "size" | "number";
  sortOrder: "asc" | "desc";
};

type SearchAndFilterProps = {
  setFilter: OnFilterChange;
  setSearch: OnFilterChange;
  setPage: (page: number) => void;
  setSort?: OnSortChange;
  type: "field" | "practice";
  practiceSlug?: PracticeSlug | PracticeSlug[];
  uniqueCrops?: string[]; // Unique crop names for filtering
  availableCropTypes?: CROP_TYPE_FCPO[]; // Available crop types for filtering
  availableYears?: number[]; // Available years for filtering (extracted from data)
};

// Year range configuration - can be made configurable via props or constants
const DEFAULT_YEAR_RANGE = { start: 2020, end: 2026 };

const SORT_OPTIONS = [
  { value: "", label: "Default order" },
  { value: "name-asc", label: "Field name (A-Z)" },
  { value: "name-desc", label: "Field name (Z-A)" },
  { value: "size-asc", label: "Size (smallest first)" },
  { value: "size-desc", label: "Size (largest first)" },
  { value: "number-asc", label: "Field number (lowest first)" },
  { value: "number-desc", label: "Field number (highest first)" },
];

export const SearchAndFilter = ({
  setFilter,
  setSearch,
  setPage,
  setSort,
  type = "field",
  practiceSlug,
  uniqueCrops = [],
  availableCropTypes = [],
  availableYears,
}: SearchAndFilterProps) => {
  const handleFilterChange = (filter: string) => {
    setFilter(filter);
    setPage(1);
  };

  const handleSearchChange = (s: string) => {
    setSearch(s);
    setPage(1);
  };

  const handleSortChange = (value: string) => {
    if (!setSort) return;
    if (!value) {
      setSort(undefined);
      return;
    }
    const [sortBy, sortOrder] = value.split("-") as ["name" | "size" | "number", "asc" | "desc"];
    setSort({ sortBy, sortOrder });
    setPage(1);
  };

  // Determine which pending filter to show based on practice type
  const getPendingFilterForPractice = (): { key: string; title: string } | null => {
    if (type === "field") {
      return { key: "pendingVerification", title: "pending verification" };
    }

    // For practice lists, show practice-specific pending filter
    if (!practiceSlug) return null;

    const slugs = Array.isArray(practiceSlug) ? practiceSlug : [practiceSlug];

    // Check for crops
    if (slugs.some((s) => s === PRACTICE_SLUG.CROPS)) {
      return { key: "pendingCrops", title: "pending crops" };
    }
    // Check for fertilizers
    if (slugs.some((s) => s === PRACTICE_SLUG.MINERAL_FERTILIZERS || s === PRACTICE_SLUG.ORGANIC_FERTILIZERS)) {
      return { key: "pendingFertilizers", title: "pending fertilizers" };
    }
    // Check for tillage & seeding
    if (slugs.some((s) => s === PRACTICE_SLUG.TILLAGES || s === PRACTICE_SLUG.SEEDINGS)) {
      return { key: "pendingTillageSeeding", title: "pending tillage & seeding" };
    }

    return null;
  };

  const pendingFilter = getPendingFilterForPractice();

  // Common editing request filter structure
  const createEditingRequestFilters = (key: string): FilterSetProps["filters"] => ({
    [key]: {
      title: "has editing requests",
      filters: {
        openEditingRequests: {
          title: "open editing requests",
        },
        toReviewEditingRequests: {
          title: "to review editing requests",
        },
        reviewedEditingRequests: {
          title: "reviewed editing requests",
        },
      },
    },
  });

  // Get editing request filter structure based on type
  const getEditingRequestFilters = (): FilterSetProps["filters"] | null => {
    if (type === "field") {
      return createEditingRequestFilters("hasFieldEditingRequests");
    }

    // For practice lists, check if it's crops
    if (!practiceSlug) return null;

    const slugs = Array.isArray(practiceSlug) ? practiceSlug : [practiceSlug];
    if (slugs.some((s) => s === PRACTICE_SLUG.CROPS)) {
      return createEditingRequestFilters("hasCropEditingRequests");
    }

    return null;
  };

  const editingRequestFilters = getEditingRequestFilters();

  // Generate years - use availableYears if provided, otherwise use default range
  const years =
    availableYears && availableYears.length > 0
      ? [...availableYears].sort((a, b) => a - b)
      : Array.from({ length: DEFAULT_YEAR_RANGE.end - DEFAULT_YEAR_RANGE.start + 1 }, (_, index) => DEFAULT_YEAR_RANGE.start + index);

  // Create crop filter structure with nested year filters
  const getCropFilters = (): FilterSetProps["filters"] | null => {
    if (type !== "practice" || !practiceSlug) return null;

    const slugs = Array.isArray(practiceSlug) ? practiceSlug : [practiceSlug];
    if (!slugs.some((s) => s === PRACTICE_SLUG.CROPS)) return null;

    // Always show the crops filter, even if crops are still loading
    // Create nested structure: crops -> [crop names] -> years -> [2020-2026]
    const cropFilters: Record<string, FilterSetProps["filters"][string]> = {};

    uniqueCrops.forEach((cropName) => {
      const yearFilters: Record<string, { title: string }> = {};
      years.forEach((year) => {
        yearFilters[`year-${year}`] = {
          title: year.toString(),
        };
      });

      cropFilters[`crop-${cropName}`] = {
        title: cropName,
        filters: yearFilters,
      };
    });

    return {
      crops: {
        title: "crops",
        filters: cropFilters,
      },
    };
  };

  // Create crop type filter structure with nested year filters
  const getCropTypeFilters = (): FilterSetProps["filters"] | null => {
    if (type !== "practice" || !practiceSlug) return null;

    const slugs = Array.isArray(practiceSlug) ? practiceSlug : [practiceSlug];
    if (!slugs.some((s) => s === PRACTICE_SLUG.CROPS)) return null;

    if (availableCropTypes.length === 0) return null;

    // Create nested structure: cropType -> [cover, undersowing, main] -> years -> [2020-2026]
    const cropTypeFilters: Record<string, FilterSetProps["filters"][string]> = {};

    availableCropTypes.forEach((cropType) => {
      const yearFilters: Record<string, { title: string }> = {};
      years.forEach((year) => {
        yearFilters[`year-${year}`] = {
          title: year.toString(),
        };
      });

      // Convert enum to lowercase for display (e.g., "COVER" -> "cover")
      const cropTypeLabel = cropType.toLowerCase();
      cropTypeFilters[`cropType-${cropType}`] = {
        title: cropTypeLabel,
        filters: yearFilters,
      };
    });

    return {
      cropType: {
        title: "crop type",
        filters: cropTypeFilters,
      },
    };
  };

  const cropFilters = getCropFilters();
  const cropTypeFilters = getCropTypeFilters();

  return (
    <>
      <Row gutter={[16, 16]} className="margin-bottom-100">
        <Col md={8} sm={24}>
          <InputSearch label={"search"} onChange={(e) => handleSearchChange(e.target.value)} />
        </Col>
        {setSort && (
          <Col md={6} sm={24}>
            {/* @ts-expect-error Select component has default values for optional props */}
            <Select label="Sort by" defaultValue="" onChange={handleSortChange} style={{ width: "100%" }}>
              {SORT_OPTIONS.map((option) => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Col>
        )}
      </Row>

      <Row className="padding-bottom-200">
        <Col>
          <Filters
            onFilterChange={handleFilterChange}
            filters={
              {
                ...(pendingFilter && {
                  [pendingFilter.key]: {
                    title: pendingFilter.title,
                  },
                }),
                ...(editingRequestFilters || {}),
                ...(cropFilters || {}),
                ...(cropTypeFilters || {}),
                allFields: {
                  title: "all fields",
                },
              } as FilterSetProps["filters"]
            }
          />
        </Col>
      </Row>
    </>
  );
};

const Filters = ({ onFilterChange, filters }: { onFilterChange: OnFilterChange; filters: FilterSetProps["filters"] }) => {
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);

  const handleFilterChange = (newFilters: string[]) => {
    setSelectedFilters(newFilters);
    // For nested filters (crop/year/cropType combinations), combine them; otherwise use the last filter key
    // If no filters selected, pass empty string to clear
    if (newFilters && newFilters.length > 0) {
      // Check for filter combinations
      const cropIndex = newFilters.findIndex((f) => f.startsWith("crop-") && !f.startsWith("cropType-"));
      const cropTypeIndex = newFilters.findIndex((f) => f.startsWith("cropType-"));
      const yearIndex = newFilters.findIndex((f) => f.startsWith("year-"));

      // Build filter parts array with consistent ordering
      const filterParts: string[] = [];
      if (cropIndex !== -1) filterParts.push(newFilters[cropIndex]);
      if (cropTypeIndex !== -1) filterParts.push(newFilters[cropTypeIndex]);
      if (yearIndex !== -1) filterParts.push(newFilters[yearIndex]);

      // Sort filter parts for consistent ordering: crop -> cropType -> year
      const filterOrder: Record<string, number> = { "crop-": 0, "cropType-": 1, "year-": 2 };
      filterParts.sort((a, b) => {
        const aPrefix = Object.keys(filterOrder).find((k) => a.startsWith(k)) || "";
        const bPrefix = Object.keys(filterOrder).find((k) => b.startsWith(k)) || "";
        return (filterOrder[aPrefix] ?? 99) - (filterOrder[bPrefix] ?? 99);
      });

      if (filterParts.length > 1) {
        // Combine multiple filters: e.g., "crop-Kartoffeln|year-2026" or "cropType-cover|year-2026"
        onFilterChange(filterParts.join("|"));
      } else if (filterParts.length === 1) {
        // Single filter
        onFilterChange(filterParts[0]);
      } else {
        // Use the last (leaf) filter key if no recognized filters found
        onFilterChange(newFilters[newFilters.length - 1]);
      }
    } else {
      onFilterChange("");
    }
  };

  const handleUnselectFilter = (index: number) => {
    // Remove the filter at the given index and all filters after it
    const newFilters = selectedFilters.slice(0, index);
    handleFilterChange(newFilters);
  };

  return (
    <FilterSet
      onClearFilters={() => handleFilterChange([])}
      onSelectFilter={(key: string) => {
        // When selecting a filter, add it to the current path
        handleFilterChange([...selectedFilters, key]);
      }}
      onUnselectFilter={handleUnselectFilter}
      selectedFilters={selectedFilters}
      filters={filters}
    />
  );
};
